import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';

const SearchAndFilters = ({
  searchTerm,
  setSearchTerm,
  viewMode,
  setViewMode,
  selectedYear,
  setSelectedYear,
  selectedSeason,
  setSelectedSeason,
  filterRank,
  setFilterRank,
  filterStatus,
  setFilterStatus,
  sortBy,
  setSortBy,
  activeFilter,
  setActiveFilter,
  years,
  suggestionsList
}) => {
  const [showSuggest, setShowSuggest] = useState(false);
  const { t } = useLanguage();

  return (
    <div className="mt-4 space-y-3">
      <div className="relative" onFocus={() => setShowSuggest(true)}>
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder={t('search')}
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setShowSuggest(true); }}
          onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        />
        {showSuggest && searchTerm && suggestionsList.length > 0 && (
          <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
            {suggestionsList.map((s, idx) => (
              <div 
                key={idx} 
                className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                onMouseDown={() => { 
                  setSearchTerm(s.text); 
                  setActiveFilter(s); 
                  setShowSuggest(false);
                }}
              >
                <span className="text-sm text-gray-500 dark:text-gray-400 mr-1">[{s.category}]</span><span className="dark:text-gray-100">{s.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {activeFilter && (
        <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 mt-1">
          <span className="bg-blue-100 dark:bg-blue-900/30 px-2 py-0.5 rounded-full font-medium">
            {activeFilter.category}
          </span>
          <span className="truncate max-w-xs dark:text-gray-100">{activeFilter.text}</span>
          <button
            onClick={() => { setActiveFilter(null); setSearchTerm(''); }}
            className="ml-1 text-blue-500 dark:text-blue-400"
          >
            ✕
          </button>
        </div>
      )}

      <div className="flex gap-3 items-center flex-wrap">
        <select
          value={viewMode}
          onChange={(e) => setViewMode(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">{t('allDisplay')}</option>
          <option value="year">{t('yearDisplay')}</option>
          <option value="season">{t('seasonDisplay')}</option>
        </select>

        {viewMode !== 'all' && (
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        )}

        {viewMode === 'season' && (
          <select
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value={t('winterValue')}>{t('winterCour')}</option>
            <option value={t('springValue')}>{t('springCour')}</option>
            <option value={t('summerValue')}>{t('summerCour')}</option>
            <option value={t('autumnValue')}>{t('autumnCour')}</option>
          </select>
        )}

        <select
          value={filterRank}
          onChange={e => setFilterRank(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">{t('noRankFilter')}</option>
          <option value="S+">S+</option>
          <option value="S">S</option>
          <option value="A">A</option>
          <option value="B">B</option>
          <option value="C">C</option>
          <option value="C-">C-</option>
          <option value="D">D</option>
        </select>

        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">{t('noStatusFilter')}</option>
          <option value="watching">{t('watching')}</option>
          <option value="complete">{t('complete')}</option>
          <option value="dropped">{t('dropped')}</option>
        </select>

        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="none">{t('sortBy')}</option>
          <option value="rankAsc">{t('sortRankAsc')}</option>
          <option value="rankDesc">{t('sortRankDesc')}</option>
          <option value="yearAsc">{t('sortYearAsc')}</option>
          <option value="yearDesc">{t('sortYearDesc')}</option>
          <option value="titleAsc">{t('sortTitleAsc')}</option>
          <option value="titleDesc">{t('sortTitleDesc')}</option>
        </select>
      </div>
    </div>
  );
};

export default SearchAndFilters;